import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HomeContent = () => {
  return (
    <div className="space-y-6 md:space-y-8 mt-12 md:mt-16">
      <section className="bg-white p-6 md:p-8 rounded-[16px] md:rounded-[24px] shadow-lg">
        <h2 className="text-2xl md:text-3xl font-bold text-[#6B4EE6] mb-4">
          What is CardCombiner?
        </h2>
        <p className="text-gray-700 leading-relaxed mb-4">
          CardCombiner is a free, browser-based image grid generator built for
          people who need to combine several photos into a single, clean
          composite. Whether you're a Pokémon or sports card seller preparing
          listings for eBay and TCGplayer, an Etsy shop owner showing product
          variations, a real-estate agent stitching together property shots, or
          a teacher building a worksheet, CardCombiner gets the job done in
          seconds without any sign-up.
        </p>
        <p className="text-gray-700 leading-relaxed mb-4">
          Everything happens locally in your browser. Your images are never
          uploaded to a server, never stored, and never shared. That means the
          tool is fast, private by default, and works just as well on a phone
          as it does on a desktop.
        </p>
        <p className="text-gray-700 leading-relaxed">
          Want to learn more about how we approach image tools and trading-card
          listings? Read about our story on the{" "}
          <Link to="/about" className="text-[#6B4EE6] underline hover:no-underline">
            About page
          </Link>{" "}
          or browse practical guides on the{" "}
          <Link to="/blog" className="text-[#6B4EE6] underline hover:no-underline">
            blog
          </Link>
          .
        </p>
      </section>

      <section className="bg-white p-6 md:p-8 rounded-[16px] md:rounded-[24px] shadow-lg">
        <h2 className="text-2xl md:text-3xl font-bold text-[#6B4EE6] mb-4">
          How to combine images into one
        </h2>
        <ol className="list-decimal pl-5 space-y-3 text-gray-700">
          <li>
            <span className="font-semibold">Upload your images.</span> Drag and
            drop up to 9 photos onto the upload area, or click to pick files
            from your device. JPEG, PNG, and WebP are all supported.
          </li>
          <li>
            <span className="font-semibold">Pick single or batch mode.</span>{" "}
            Single mode merges everything into one collage. Batch mode lets you
            upload many images and split them into multiple collages
            automatically (for example: 27 photos with 3 per collage gives you
            9 collages in a ZIP file).
          </li>
          <li>
            <span className="font-semibold">Reorder if needed.</span> The grid
            keeps the order in which you uploaded the images, so you stay in
            control of the layout.
          </li>
          <li>
            <span className="font-semibold">Choose an output format.</span>{" "}
            JPEG is best for sharing and small files, PNG keeps maximum
            quality, and WebP gives you a great balance of size and clarity.
          </li>
          <li>
            <span className="font-semibold">Download and use.</span> Click
            download and your finished collage saves straight to your device,
            ready to upload to a marketplace, social post, or email.
          </li>
        </ol>
      </section>

      <section className="bg-white p-6 md:p-8 rounded-[16px] md:rounded-[24px] shadow-lg">
        <h2 className="text-2xl md:text-3xl font-bold text-[#6B4EE6] mb-4">
          Who uses CardCombiner?
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-[#6B4EE6] mb-2">
              Trading card sellers
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              Combine front, back, and close-up shots of a Pokémon, Magic, or
              sports card into one listing image. Buyers see condition at a
              glance and you spend less time per listing.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-[#6B4EE6] mb-2">
              Marketplace and Etsy shops
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              Show product variations, sizes, or color options together in a
              single hero image so shoppers don't have to swipe through a
              gallery.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-[#6B4EE6] mb-2">
              Real-estate agents
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              Build quick photo summaries of a property for social posts and
              ads, with kitchen, living room, and exterior side by side.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-[#6B4EE6] mb-2">
              Teachers and students
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              Put before/after diagrams, screenshots, or worksheet visuals on
              one page without opening Photoshop.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-[#6B4EE6] mb-2">
              Social media creators
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              Build photo-dump style grids for Instagram, X, or Pinterest in
              the right aspect ratio without third-party apps.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-[#6B4EE6] mb-2">
              Anyone comparing things
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed">
              From recipes to tattoo references to outfit options, putting
              several pictures side by side makes decisions easier.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white p-6 md:p-8 rounded-[16px] md:rounded-[24px] shadow-lg">
        <h2 className="text-2xl md:text-3xl font-bold text-[#6B4EE6] mb-4">
          Frequently asked questions
        </h2>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="q1">
            <AccordionTrigger className="text-left">
              How many images can I combine at once?
            </AccordionTrigger>
            <AccordionContent className="text-gray-700">
              In single mode you can combine up to 9 images into one collage.
              In batch mode there is no fixed cap on the total number of
              images — you choose how many photos go into each collage, and
              CardCombiner generates as many collages as your upload allows
              and packages them in a ZIP file.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q2">
            <AccordionTrigger className="text-left">
              Which image formats are supported?
            </AccordionTrigger>
            <AccordionContent className="text-gray-700">
              You can upload JPEG, PNG, and WebP images. The finished collage
              can be downloaded as JPEG (smallest file, great for sharing),
              PNG (lossless quality), or WebP (modern, well-balanced format
              supported by all major browsers).
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q3">
            <AccordionTrigger className="text-left">
              Are my images uploaded to a server?
            </AccordionTrigger>
            <AccordionContent className="text-gray-700">
              No. Every image stays on your device. CardCombiner does all of
              the resizing, layout, and rendering inside your browser using
              the HTML canvas API. Nothing is uploaded, stored, or shared with
              any third party.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q4">
            <AccordionTrigger className="text-left">
              Can I keep my images in a specific order?
            </AccordionTrigger>
            <AccordionContent className="text-gray-700">
              Yes. The collage uses the same order in which you added the
              images, so you can plan your layout up front. If you want a
              different arrangement, just remove and re-add files in the
              order you want.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q5">
            <AccordionTrigger className="text-left">
              Does CardCombiner work on mobile?
            </AccordionTrigger>
            <AccordionContent className="text-gray-700">
              Yes. The interface is fully responsive, so it works in mobile
              Safari, Chrome, and most modern smartphone browsers. On iOS,
              the downloaded image opens in a new tab so you can long-press
              and save it to your Photos.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q6">
            <AccordionTrigger className="text-left">
              Why is there a small watermark on the result?
            </AccordionTrigger>
            <AccordionContent className="text-gray-700">
              We add a small "Created with CardCombiner.com" tag in the corner
              so people can find the tool. It's intentionally subtle and
              positioned out of the way of your main subject.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q7">
            <AccordionTrigger className="text-left">
              Is CardCombiner really free?
            </AccordionTrigger>
            <AccordionContent className="text-gray-700">
              Yes — there is no sign-up, no paywall, and no usage limit. The
              tool is supported by ads on the page so we can keep it free for
              everyone.
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="q8">
            <AccordionTrigger className="text-left">
              Will the output quality match my originals?
            </AccordionTrigger>
            <AccordionContent className="text-gray-700">
              The collage canvas is sized using the largest dimensions of
              your input images, so quality is preserved as much as possible.
              For maximum fidelity, choose PNG; for the smallest file, choose
              JPEG.
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>
    </div>
  );
};

export default HomeContent;